import React from "react"
import { normalizarId } from "../hooks/useScope"
import { getScope } from "../hooks/useScope"
import { useState, useEffect } from "react"
import { Outlet } from "react-router-dom"
import { styled } from "@mui/material/styles"
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem as MuiMenuItem,
  Avatar,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
} from "@mui/material"
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  Category as CategoryIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Pets as PetsIcon,
  Videocam as VideoCamIcon,
  CameraAlt as CameraAltIcon,
  SwapHoriz as SwapHorizIcon,
  Fence as FenceIcon,
  Public as PublicIcon,
  Domain as DomainIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
} from "@mui/icons-material"
import { useAuth } from "../contexts/AuthContext"
import { useCryptoNavigation } from "../App"
import { useColorMode } from "../theme/colorMode"
import Icon from "@mdi/react"
import { mdiBarn, mdiHorseVariant } from "@mdi/js"
import ChangePasswordDialog from "../components/ChangePassword"
import LanguageSelector from "../components/LanguageSelector"
import { useTranslation } from "react-i18next"
import { UserData } from "../config"
import logoBranco from "../assets/images/logo branco.png"
import api from "../services/api"
import type { Tenant } from "../interfaces/index"

// Tipo de acesso
type AccessType = 'general' | 'specific'

const drawerWidth = 240

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })<{
  open?: boolean
}>(({ theme, open }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
    marginLeft: 0,
  },
  ...(open && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
}))

const AppBarStyled = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<{
  open?: boolean
}>(({ theme, open }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}))

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}))

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}))

// Componente de Seleção de Tenant
const TenantSelector: React.FC<{
  onAccessSelected: (accessType: AccessType, tenantId?: string, tenantName?: string) => void
}> = ({ onAccessSelected }) => {
  const { t } = useTranslation()
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [accessType, setAccessType] = useState<AccessType>('general')
  const [selectedTenantId, setSelectedTenantId] = useState<string>('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadTenants = async () => {
      try {
        const response = await api.get('/tenant')
        
        let tenantList: Tenant[] = []
        if (Array.isArray(response.data)) {
          tenantList = response.data
        } else if (response.data?.tenants && Array.isArray(response.data.tenants)) {
          tenantList = response.data.tenants
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          tenantList = response.data.data
        }
        
        // Sem recorte por parceiro aqui: o GET /tenant já vem filtrado pelo
        // servidor, pelo token. Filtrar de novo no navegador não acrescentaria
        // segurança e ESCONDERIA proprietários válidos que ainda não tenham o
        // campo de parceiro preenchido.
        setTenants(tenantList)
      } catch (error) {
        console.error('Erro ao carregar proprietários:', error)
      } finally {
        setLoading(false)
      }
    }

    loadTenants()
  }, [])

  const handleConfirm = () => {
    if (accessType === 'general') {
      onAccessSelected('general')
    } else if (accessType === 'specific' && selectedTenantId) {
      const selectedTenant = tenants.find(t => String(t.tenantId) === selectedTenantId)
      if (selectedTenant) {
        onAccessSelected('specific', selectedTenantId, selectedTenant.tenantName)
      }
    }
  }

  const canConfirm = accessType === 'general' || (accessType === 'specific' && selectedTenantId)

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 600,
          width: '100%',
          mx: 2,
        }}
      >
        <Typography variant="h5" gutterBottom align="center" sx={{ mb: 3 }}>
          {t("dashboard.accessSelectTitle")}
        </Typography>
        
        <Alert severity="info" sx={{ mb: 3 }}>
          {t("dashboard.accessInfo")}
        </Alert>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
              <FormLabel component="legend" sx={{ mb: 2 }}>{t("dashboard.accessTypeLabel")}</FormLabel>
              <RadioGroup
                value={accessType}
                onChange={(e) => {
                  setAccessType(e.target.value as AccessType)
                  setSelectedTenantId('') // Limpar seleção ao mudar tipo
                }}
              >
                <FormControlLabel 
                  value="general" 
                  control={<Radio />} 
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {t("dashboard.accessGeneral")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("dashboard.accessGeneralDesc")}
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel 
                  value="specific" 
                  control={<Radio />} 
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {t("dashboard.accessSpecific")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("dashboard.accessSpecificDesc")}
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>

            {accessType === 'specific' && (
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>{t("dashboard.ownerLabel")}</InputLabel>
                <Select
                  value={selectedTenantId}
                  label={t("dashboard.ownerLabel")}
                  onChange={(e) => setSelectedTenantId(e.target.value)}
                >
                  {tenants.map((tenant) => (
                    <MuiMenuItem key={tenant.tenantId} value={String(tenant.tenantId)}>
                      {tenant.tenantName}
                    </MuiMenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={handleConfirm}
              disabled={!canConfirm}
            >
              {t("common.confirm")}
            </Button>
          </>
        )}
      </Paper>
    </Box>
  )
}

// Componente DashboardCards
export const DashboardCards = () => {
  const { t } = useTranslation()
  const { navigateToPage } = useCryptoNavigation()

  const getAccessType = (): AccessType => {
    // Primeiro verificar se o usuário tem tenant associado
    try {
      const userStr = localStorage.getItem('user')
      if (userStr) {
        const user = JSON.parse(userStr)
        const userTenant = user?.userTenant
        if (userTenant) {
          console.log('🎯 DashboardCards: Usuário tem tenant associado:', userTenant, '- retornando "specific"')
          return 'specific'
        }
      }
    } catch (e) {
      console.error('Erro ao verificar userTenant:', e)
    }
    
    // Se não tem tenant associado, usar o localStorage
    const savedAccessType = (localStorage.getItem('accessType') as AccessType) || 'general'
    console.log('🎯 DashboardCards: AccessType do localStorage:', savedAccessType)
    return savedAccessType
  }

  const accessType = getAccessType()
  console.log('🎯 DashboardCards: AccessType final usado:', accessType)
  // Papéis 1 (admin) e 2 (manager) agem; o resto é leitura. Era uma lista de
  // NEGAÇÃO por nome ("Viewer", "Alert"), que liberava qualquer papel fora
  // dela — inclusive um criado depois — e divergia do app, que sempre usou
  // lista de permissão por número. A regra agora é única (ver useScope).
  const shouldDisableActions = !getScope().canManageDevices

  const createMenuHandler = (pageName: string, requiresTenant: boolean = false) => {
    return () => {
      console.log(`Navegando para: ${pageName}`)
      
      const params: any = { mode: 'list' }
      
      if (requiresTenant) {
        const savedTenant = localStorage.getItem('selectedTenantId')
        if (savedTenant) {
          params.tenantId = savedTenant
        }
      }

      navigateToPage(pageName, params)
    }
  }

  const renderIcon = (iconElement: JSX.Element) => {
    if (iconElement.type === Icon) {
      return React.cloneElement(iconElement, { size: 2 })
    }
    return iconElement
  }

  // Escopo do usuário: decide o que aparece de parceiro e de catálogo global.
  const escopo = getScope()

  // Itens disponíveis para acesso geral.
  //
  // Parceiros só aparece para quem pode administrá-los. Tipos de proprietário e
  // raças de cavalo são catálogos do grupo RAIZ: um usuário de parceiro != 0
  // não os vê, porque são compartilhados entre todos os parceiros.
  const generalAccessItems = [
    // Visível também quando o parceiro é DESCONHECIDO, para o admin chegar à
    // tela e ler por que está bloqueado — esconder sem explicar faria parecer
    // que a função não existe.
    // Quem PERTENCE a um parceiro não vê a opção: administrar parceiros não é
    // função dele, e oferecer para depois negar é só ruído.
    //
    // A exceção é o parceiro DESCONHECIDO (o backend ainda não envia o campo):
    // aí o item aparece e a tela explica, porque é estado transitório que
    // precisa ser diagnosticável.
    ...(escopo.canManagePartners ||
    (escopo.userRole === 1 && !escopo.partnerKnown)
      ? [{
          text: t("nav.partners"),
          icon: <BusinessIcon />,
          onClick: createMenuHandler("partners"),
          category: 'general'
        }]
      : []),
    ...(escopo.canManageGlobalCatalogs
      ? [{
          text: t("nav.tenantTypes"),
          icon: <CategoryIcon />,
          onClick: createMenuHandler("tipos"),
          category: 'general'
        }]
      : []),
    { 
      text: t("nav.tenants"), 
      icon: <BusinessIcon />, 
      onClick: createMenuHandler("proprietarios"),
      category: 'general'
    },
    ...(escopo.canManageGlobalCatalogs
      ? [{
          text: t("nav.horseBreeds"),
          icon: <PetsIcon />,
          onClick: createMenuHandler("racas"),
          category: 'general'
        }]
      : []),
    { 
      text: t("nav.users"), 
      icon: <PersonIcon />, 
      onClick: createMenuHandler("usuarios"),
      category: 'general'
    },
  ]

  // Itens disponíveis para acesso específico a um tenant
  const specificAccessItems = [
    { 
      text: t("nav.stables"), 
      icon: <Icon path={mdiBarn} size={1} />, 
      onClick: createMenuHandler("estabulos", true),
      category: 'specific'
    },
    { 
      text: t("nav.stalls"), 
      icon: <FenceIcon />, 
      onClick: createMenuHandler("baias", true),
      category: 'specific'
    },
    { 
      text: t("nav.horses"), 
      icon: <Icon path={mdiHorseVariant} size={1} />, 
      onClick: createMenuHandler("cavalos", true),
      category: 'specific'
    },
    { 
      text: t("nav.recorders"), 
      icon: <VideoCamIcon />, 
      onClick: createMenuHandler("gravadores", true),
      category: 'specific'
    },
    { 
      text: t("nav.cameras"), 
      icon: <CameraAltIcon />, 
      onClick: createMenuHandler("cameras", true),
      category: 'specific'
    },
    { 
      text: t("nav.users"), 
      icon: <PersonIcon />, 
      onClick: createMenuHandler("usuarios"),
      category: 'specific'
    },
  ]

  // Definir quais itens mostrar baseado no tipo de acesso
  let menuItems = []
  if (accessType === 'general') {
    menuItems = generalAccessItems
  } else {
    menuItems = specificAccessItems
  }

  const filteredItems = menuItems.filter((item) =>
    shouldDisableActions ? item.text === t('nav.cameras') : true
  )

  return (
    <Box sx={{ flexGrow: 1 }}>        
      <Grid container spacing={3}>
        {filteredItems.map((item) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={item.text}>
            <StyledCard>
              <CardActionArea
                onClick={item.onClick}
                sx={{ height: '100%', p: 2 }}
              >
                <CardContent sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  textAlign: 'center',
                  gap: 2
                }}>
                  <Box sx={{ 
                    color: 'primary.main',
                    '& svg': { fontSize: 48 }
                  }}>
                    {renderIcon(item.icon)}
                  </Box>
                  <Typography variant="h6" component="div">
                    {item.text}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </StyledCard>
          </Grid>
        ))}
      </Grid>
    </Box>
  )
}

// Componente Principal - Dashboard
// Função auxiliar para obter tenantId do usuário
const getUserTenantIdHelper = (): number | null => {
  try {
    const userStr = localStorage.getItem('user')
    console.log('📦 localStorage.getItem("user"):', userStr)
    
    if (userStr) {
      const user = JSON.parse(userStr)
      console.log('👤 Objeto user parseado:', user)
      console.log('🔍 user.userTenant:', user?.userTenant)
      console.log('🔍 user.tenantId:', user?.tenantId)
      console.log('🔍 user.tenant_id:', user?.tenant_id)
      
      // Tentar várias possibilidades de nome do campo (userTenant é o correto!).
      // Encadear com `||` descartaria o ZERO e cairia no apelido seguinte.
      const tenantId = normalizarId(
        user?.userTenant,
        user?.tenantId,
        user?.tenant_id,
        user?.TenantId,
        user?.Tenant_id,
        user?.TENANT_ID,
      )
      
      console.log('✅ tenantId final:', tenantId)
      return tenantId
    }
  } catch (e) {
    console.error('❌ Erro ao obter tenantId do usuário:', e)
  }
  console.log('⚠️ Retornando null - não encontrou user no localStorage')
  return null
}

export default function Dashboard() {
  const { t } = useTranslation()
  const { mode, toggle: toggleColorMode } = useColorMode()
  const [open, setOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false)
  const [accessType, setAccessType] = useState<AccessType>(() => {
    // Inicializar baseado no tenant do usuário ou localStorage
    const userTenantId = getUserTenantIdHelper()
    if (userTenantId) {
      console.log('🎯 Inicializando accessType como "specific" - usuário tem tenant:', userTenantId)
      return 'specific'
    }
    const savedAccessType = localStorage.getItem('accessType') as AccessType
    console.log('🎯 Inicializando accessType do localStorage:', savedAccessType || 'general (padrão)')
    return savedAccessType || 'general'
  })
  const [selectedTenant, setSelectedTenant] = useState<string | null>(() => {
    // Inicializar baseado no tenant do usuário ou localStorage
    const userTenantId = getUserTenantIdHelper()
    if (userTenantId) {
      const tenantId = String(userTenantId)
      console.log('🎯 Inicializando selectedTenant:', tenantId)
      return tenantId
    }
    const savedTenant = localStorage.getItem('selectedTenantId')
    console.log('🎯 Inicializando selectedTenant do localStorage:', savedTenant)
    return savedTenant
  })
  const [selectedTenantName, setSelectedTenantName] = useState<string | null>(() => {
    // Inicializar do localStorage se disponível
    const savedTenantName = localStorage.getItem('selectedTenantName')
    console.log('🎯 Inicializando selectedTenantName do localStorage:', savedTenantName)
    return savedTenantName
  })
  const [showTenantSelector, setShowTenantSelector] = useState(false)
  const [userHasTenant, setUserHasTenant] = useState<boolean>(() => {
    // Inicializar baseado no tenantId do usuário
    const userTenantId = getUserTenantIdHelper()
    const hasTenant = userTenantId !== null
    console.log('Estado inicial userHasTenant:', hasTenant, 'userTenantId:', userTenantId)
    return hasTenant
  })
  const { user, logout } = useAuth()
  const { navigateToPage, currentPage } = useCryptoNavigation()
  const [isMobile, setIsMobile] = useState(false)

  const getUserTenantId = (): number | null => {
    return getUserTenantIdHelper()
  }

  // Papéis 1 (admin) e 2 (manager) agem; o resto é leitura. Era uma lista de
  // NEGAÇÃO por nome ("Viewer", "Alert"), que liberava qualquer papel fora
  // dela — inclusive um criado depois — e divergia do app, que sempre usou
  // lista de permissão por número. A regra agora é única (ver useScope).
  const shouldDisableActions = !getScope().canManageDevices

  const createMenuHandler = (pageName: string, requiresTenant: boolean = false) => {
    return () => {
      const params: any = { mode: 'list' }
      
      if (requiresTenant && selectedTenant) {
        params.tenantId = selectedTenant
      }

      navigateToPage(pageName, params)

      if (isMobile) {
        handleDrawerClose()
      }
    }
  }

  // Itens do menu lateral baseados no tipo de acesso
  const getMenuItems = () => {
    // Mesmo recorte dos cards: parceiros para quem administra, catálogos
    // globais (tipos e raças) só no grupo raiz.
    const escopoMenu = getScope()
    const generalItems = [
      ...(escopoMenu.canManagePartners ||
      (escopoMenu.userRole === 1 && !escopoMenu.partnerKnown)
        ? [{
            text: t("nav.partners"),
            icon: <BusinessIcon />,
            onClick: createMenuHandler("partners")
          }]
        : []),
      ...(escopoMenu.canManageGlobalCatalogs
        ? [{
            text: t("nav.tenantTypes"),
            icon: <CategoryIcon />,
            onClick: createMenuHandler("tipos")
          }]
        : []),
      { 
        text: t("nav.tenants"), 
        icon: <BusinessIcon />, 
        onClick: createMenuHandler("proprietarios")
      },
      ...(escopoMenu.canManageGlobalCatalogs
        ? [{
            text: t("nav.horseBreeds"),
            icon: <PetsIcon />,
            onClick: createMenuHandler("racas")
          }]
        : []),
      { 
        text: t("nav.users"), 
        icon: <PersonIcon />, 
        onClick: createMenuHandler("usuarios")
      },
    ]

    const specificItems = [
      { 
        text: t("nav.stables"), 
        icon: <Icon path={mdiBarn} size={1} />, 
        onClick: createMenuHandler("estabulos", true)
      },
      { 
        text: t("nav.stalls"), 
        icon: <FenceIcon />, 
        onClick: createMenuHandler("baias", true)
      },
      { 
        text: t("nav.horses"), 
        icon: <Icon path={mdiHorseVariant} size={1} />, 
        onClick: createMenuHandler("cavalos", true)
      },
      { 
        text: t("nav.recorders"), 
        icon: <VideoCamIcon />, 
        onClick: createMenuHandler("gravadores", true)
      },
      { 
        text: t("nav.cameras"), 
        icon: <CameraAltIcon />, 
        onClick: createMenuHandler("cameras", true)
      },
      { 
        text: t("nav.users"), 
        icon: <PersonIcon />, 
        onClick: createMenuHandler("usuarios")
      },
    ]

    return accessType === 'general' ? generalItems : specificItems
  }

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 600)
    }

    window.addEventListener("resize", handleResize)
    handleResize()

    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // Inicializar configuração ao montar o componente
  useEffect(() => {
    console.log('=== INICIALIZAÇÃO DO COMPONENTE ===')
    console.log('📋 Todas as chaves do localStorage:', Object.keys(localStorage))
    
    // Verificar todas as chaves que podem conter informação de tenant
    const keysToCheck = ['user', 'userData', 'currentUser', 'authUser', 'tenant', 'selectedTenant']
    keysToCheck.forEach(key => {
      const value = localStorage.getItem(key)
      if (value) {
        console.log(`🔑 localStorage["${key}"]:`, value)
      }
    })
    
    const userTenantId = getUserTenantId()
    console.log('userTenantId detectado:', userTenantId)
    
    // Definir userHasTenant logo no início
    if (userTenantId) {
      console.log('Definindo userHasTenant = true')
      setUserHasTenant(true)
      
      // IMPORTANTE: Salvar no localStorage imediatamente
      const tenantId = String(userTenantId)
      localStorage.setItem('accessType', 'specific')
      localStorage.setItem('selectedTenantId', tenantId)
      console.log('💾 localStorage atualizado com accessType=specific e selectedTenantId=' + tenantId)
    } else {
      console.log('Definindo userHasTenant = false')
      setUserHasTenant(false)
    }
    console.log('=== FIM DA INICIALIZAÇÃO ===')
  }, [])

  // Verificar se precisa mostrar seletor de tenant
  useEffect(() => {
    const savedAccessType = localStorage.getItem('accessType') as AccessType
    const savedTenant = localStorage.getItem('selectedTenantId')
    const savedTenantName = localStorage.getItem('selectedTenantName')
    const userTenantId = getUserTenantId()

    console.log('Verificando configuração de acesso:', {
      savedAccessType,
      savedTenant,
      savedTenantName,
      userTenantId,
      currentPage,
      userHasTenant
    })

    // Se o usuário tem tenant associado, configurar automaticamente
    if (userTenantId) {
      console.log('Usuário tem tenant associado:', userTenantId)
      const tenantId = String(userTenantId)
      setAccessType('specific')
      setSelectedTenant(tenantId)
      localStorage.setItem('accessType', 'specific')
      localStorage.setItem('selectedTenantId', tenantId)
      
      // Buscar nome do tenant se ainda não tiver
      if (!savedTenantName || savedTenant !== tenantId) {
        api.get(`/tenant/${userTenantId}`)
          .then(response => {
            const tenantName = response.data?.tenantName || response.data?.tenant?.tenantName || t("dashboard.ownerLabel")
            setSelectedTenantName(tenantName)
            localStorage.setItem('selectedTenantName', tenantName)
            console.log('Nome do tenant definido:', tenantName)
          })
          .catch(err => console.error('Erro ao buscar nome do tenant:', err))
      } else {
        setSelectedTenantName(savedTenantName)
        console.log('Nome do tenant recuperado do localStorage:', savedTenantName)
      }
      
      // IMPORTANTE: Não mostrar seletor mesmo se estiver no dashboard
      setShowTenantSelector(false)
      console.log('Seletor ocultado - usuário tem tenant')
    } 
    // Se não tem tenant associado, verificar se já tem configuração salva
    else if (savedAccessType) {
      setAccessType(savedAccessType)
      
      if (savedAccessType === 'specific' && savedTenant && savedTenantName) {
        setSelectedTenant(savedTenant)
        setSelectedTenantName(savedTenantName)
        setShowTenantSelector(false)
        console.log('Configuração específica recuperada:', savedTenantName)
      } else if (savedAccessType === 'general') {
        setSelectedTenant(null)
        setSelectedTenantName(null)
        setShowTenantSelector(false)
        console.log('Configuração geral recuperada')
      } else if (currentPage === 'dashboard') {
        setShowTenantSelector(true)
        console.log('Mostrando seletor - configuração incompleta')
      }
    } 
    // Se não tem nada configurado, mostrar seletor no dashboard
    else if (currentPage === 'dashboard') {
      setShowTenantSelector(true)
      console.log('Mostrando seletor - sem configuração')
    }
  }, [currentPage, userHasTenant])

  const handleDrawerOpen = () => {
    setOpen(true)
  }

  const handleDrawerClose = () => {
    setOpen(false)
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleChangePassword = () => {
    setOpenPasswordDialog(true)
    handleMenuClose()
  }

  const handleLogout = () => {
    logout()
    localStorage.removeItem('selectedTenantId')
    localStorage.removeItem('selectedTenantName')
    localStorage.removeItem('accessType')
    localStorage.removeItem('n0-encrypted-state')
    localStorage.removeItem('n0-shareable-url')

    // CARGA COMPLETA, não navegação do roteador.
    //
    // O navegador só preenche credenciais salvas quando o DOCUMENTO carrega.
    // Saindo pelo roteador, para ele nada aconteceu — é a mesma página de
    // antes —, então o preenchimento automático não roda e quem entra de novo
    // precisa digitar e escolher na mão.
    //
    // O login mora na raiz (não está em PAGE_URL_MAPPING), que renderiza a
    // tela de entrada quando não há sessão. De quebra, recarregar garante que
    // nada do usuário anterior sobreviva em memória.
    window.location.assign('/')
  }

  const handleAccessSelected = (newAccessType: AccessType, tenantId?: string, tenantName?: string) => {
    console.log('Acesso selecionado:', { newAccessType, tenantId, tenantName })
    
    setAccessType(newAccessType)
    localStorage.setItem('accessType', newAccessType)
    
    if (newAccessType === 'general') {
      setSelectedTenant(null)
      setSelectedTenantName(null)
      localStorage.removeItem('selectedTenantId')
      localStorage.removeItem('selectedTenantName')
    } else if (newAccessType === 'specific' && tenantId && tenantName) {
      setSelectedTenant(tenantId)
      setSelectedTenantName(tenantName)
      localStorage.setItem('selectedTenantId', tenantId)
      localStorage.setItem('selectedTenantName', tenantName)
    }
    
    setShowTenantSelector(false)
  }

  const handleChangeAccess = () => {
    console.log('handleChangeAccess chamado - userHasTenant:', userHasTenant)
    handleMenuClose()
    
    // Se usuário tem tenant associado, não pode trocar
    if (userHasTenant) {
      console.log('Bloqueado no menu - usuário tem tenant associado')
      alert(t("dashboard.tenantLockedAlert"))
      return
    }
    
    console.log('Limpando configurações para mostrar seletor')
    // Limpar configuração atual
    setSelectedTenant(null)
    setSelectedTenantName(null)
    localStorage.removeItem('selectedTenantId')
    localStorage.removeItem('selectedTenantName')
    localStorage.removeItem('accessType')
    
    // Voltar ao dashboard e mostrar seletor
    navigateToPage('dashboard')
    setShowTenantSelector(true)
  }

  const handleDashboardClick = () => {
    console.log('Clicou no logo/título - voltando ao dashboard')
    navigateToPage('dashboard')
  }

  const handleChipClick = () => {
    console.log('Chip clicado - userHasTenant:', userHasTenant)
    
    // Se usuário tem tenant associado, não pode trocar
    if (userHasTenant) {
      console.log('Bloqueado - usuário tem tenant associado')
      alert(t("dashboard.tenantLockedAlert"))
      return
    }
    
    console.log('Permitido - abrindo seletor de acesso')
    handleChangeAccess()
  }

  // Determinar texto e ícone do chip
  const getChipLabel = () => {
    if (accessType === 'general') {
      return t("dashboard.accessGeneral")
    } else if (selectedTenantName) {
      return selectedTenantName
    }
    return t("dashboard.noAccess")
  }

  const getChipIcon = () => {
    return accessType === 'general' ? <PublicIcon /> : <DomainIcon />
  }

  return (
    <Box sx={{ display: "flex" }}>
      <AppBarStyled position="fixed" open={open}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{ mr: 2, ...(open && { display: "none" }) }}
          >
            <MenuIcon />
          </IconButton>
          
          <Box
            component="img"
            // A AppBar é escura em ambos os temas (azul primário no claro, superfície
            // escura no escuro), então a logo é sempre a branca. A versão charcoal
            // (logo_horsetech) é só para fundos claros — usada no login modo claro.
            src={logoBranco}
            alt="Logo"
            onClick={handleDashboardClick}
            sx={{
              // Alinhado à logo do app Flutter (top bar = 32px); um leve
              // aumento no desktop compensa a margem de respiro da PNG aparada.
              height: { xs: 24, sm: 30, md: 36 },
              maxHeight: '40px',
              width: 'auto',
              marginRight: 2,
              cursor: 'pointer',
            }}
          />
          
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              flexGrow: 1,
              fontSize: { xs: "0.85rem", sm: "1rem", md: "1.15rem" },
              fontWeight: 500,
              letterSpacing: "0.01em",
              cursor: "pointer",
              opacity: 0.92,
            }}
            onClick={handleDashboardClick}
          >
            {t("dashboard.title")}
          </Typography>

          {/* Mostrar chip de acesso (geral ou tenant específico) */}
          {(accessType === 'general' || selectedTenantName) && (
            <Chip
              icon={getChipIcon()}
              label={getChipLabel()}
              onClick={userHasTenant ? undefined : handleChipClick}
              size="small"
              sx={{
                mr: 2,
                display: { xs: 'none', sm: 'flex' },
                bgcolor: accessType === 'general' 
                  ? 'rgba(76, 175, 80, 0.2)' 
                  : userHasTenant 
                    ? 'rgba(255, 152, 0, 0.2)' // Laranja para usuário com tenant fixo
                    : 'rgba(33, 150, 243, 0.2)',
                color: 'white',
                cursor: userHasTenant ? 'default' : 'pointer',
                opacity: userHasTenant ? 0.8 : 1,
                '&:hover': userHasTenant ? {
                  opacity: 0.8, // Mantém a mesma opacidade no hover
                } : {
                  bgcolor: accessType === 'general' 
                    ? 'rgba(76, 175, 80, 0.3)' 
                    : 'rgba(33, 150, 243, 0.3)',
                },
                pointerEvents: userHasTenant ? 'none' : 'auto',
                border: userHasTenant ? '1px solid rgba(255, 152, 0, 0.5)' : 'none',
              }}
            />
          )}

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title={mode === "dark" ? t("common.lightMode") : t("common.darkMode")} arrow>
              <IconButton color="inherit" onClick={toggleColorMode} size="small">
                {mode === "dark" ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </Tooltip>
            <LanguageSelector />
            <Tooltip title={`${user?.Nome} · ${user?.Role}`} arrow>
              <IconButton
                onClick={handleMenuOpen}
                size="small"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                sx={{
                  p: 0,
                  transition: "opacity 0.2s",
                  "&:hover": { opacity: 0.85 },
                }}
              >
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: "#0066FF",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "1rem",
                    boxShadow: "0 0 0 2px rgba(255,255,255,0.25)",
                  }}
                >
                  {user?.Nome?.charAt(0)?.toUpperCase() || "U"}
                </Avatar>
              </IconButton>
            </Tooltip>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              keepMounted
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{
                elevation: 3,
                sx: { minWidth: 220, borderRadius: 2, mt: 0.5 },
              }}
            >
              {/* Cabeçalho com nome e perfil */}
              <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
                <Typography variant="subtitle2" fontWeight={700} noWrap>
                  {user?.Nome}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user?.Role}
                </Typography>
              </Box>

              {!userHasTenant && (
                <MuiMenuItem onClick={handleChangeAccess} sx={{ mt: 0.5 }}>
                  <SwapHorizIcon sx={{ mr: 1.5, fontSize: 18 }} />
                  {t("dashboard.menuChangeAccess")}
                </MuiMenuItem>
              )}
              <MuiMenuItem onClick={handleChangePassword}>
                <PersonIcon sx={{ mr: 1.5, fontSize: 18 }} />
                {t("dashboard.menuChangePassword")}
              </MuiMenuItem>
              <Divider />
              <MuiMenuItem onClick={handleLogout} sx={{ color: "error.main" }}>
                <Typography variant="body2" color="error">{t("dashboard.menuLogout")}</Typography>
              </MuiMenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBarStyled>
      
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
          },
        }}
        variant={isMobile ? "temporary" : "persistent"}
        anchor="left"
        open={open}
        onClose={isMobile ? handleDrawerClose : undefined}
      >
        <DrawerHeader>
          <IconButton onClick={handleDrawerClose}>
            <ChevronLeftIcon />
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List>
          {getMenuItems()
            .filter((item) =>
              shouldDisableActions ? item.text === t('nav.cameras') : true
            )
            .map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  onClick={item.onClick}
                  sx={{
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
        </List>
      </Drawer>
      
      <Main open={open}>
        <DrawerHeader />
        {showTenantSelector && currentPage === 'dashboard' ? (
          <TenantSelector onAccessSelected={handleAccessSelected} />
        ) : (
          <Outlet context={{ onAccessSelected: handleAccessSelected, selectedTenant, accessType }} />
        )}
      </Main>

      <ChangePasswordDialog
        open={openPasswordDialog}
        onClose={() => setOpenPasswordDialog(false)}
        username={user?.Nome}
      />
    </Box>
  )
}